import posthog, { type CaptureResult } from "posthog-js";

import { clientEnv } from "@/config/env";

/**
 * PostHog for Apex 25 (tryapex25.com + recruit.theresume.io).
 *
 * Privacy rules:
 *   - Session recordings mask every input and, on the app, all rendered text: candidate names,
 *     resumes, transcripts and notes appear all over the app, so masking everything is the only
 *     rule that cannot miss one. Images, video, canvases and iframes (resume/document previews)
 *     are blocked, and cross-origin iframes are not recorded.
 *   - Autocapture does not collect element text or attributes on the app, for the same reason.
 *   - URLs sent with any event keep only campaign parameters (utm_*, gclid): invitation tokens and
 *     other query values never leave the browser.
 *   - People are identified by internal Recruiter.id; email is a person property only.
 * The marketing hosts have no candidate data, so text stays readable in their recordings.
 */
const URL_PROPERTY = /(url|referrer|href)$/i;

const cleanUrl = (value: string) => {
  try {
    const url = new URL(value);
    const kept = new URLSearchParams();
    url.searchParams.forEach((paramValue, key) => {
      if (key.startsWith("utm_") || key === "gclid") kept.set(key, paramValue);
    });
    const query = kept.toString();
    return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
  } catch {
    return value;
  }
};

export const stripUrlQueries = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return event;
  for (const [key, value] of Object.entries(event.properties ?? {})) {
    if (typeof value === "string" && URL_PROPERTY.test(key) && /^https?:\/\//.test(value)) {
      event.properties[key] = cleanUrl(value);
    }
  }
  return event;
};

export const initPosthog = ({ marketingHost }: { marketingHost: boolean }) => {
  const key = clientEnv.posthogApiKey;
  if (!key) return false;

  posthog.init(key, {
    api_host: clientEnv.posthogHost,
    autocapture: true,
    // Sent by the router tracker (page_viewed) so SPA navigations are counted once each.
    capture_pageview: false,
    capture_pageleave: true,
    mask_all_text: !marketingHost,
    mask_all_element_attributes: !marketingHost,
    before_send: stripUrlQueries,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: marketingHost ? "[data-ph-mask]" : "*",
      blockSelector: "img:not([data-ph-unblock]), video, canvas, iframe, [data-ph-block]",
      recordCrossOriginIframes: false,
    },
    capture_performance: {
      network_timing: true,
      web_vitals: true,
    },
    persistence: "localStorage",
    disable_session_recording: false,
    advanced_disable_feature_flags_on_first_load: false,
    loaded: (instance) => {
      if (import.meta.env.DEV) instance.debug();
    },
  });
  return true;
};

/**
 * Link to this visit's PostHog session recording, or undefined when PostHog isn't running (no key,
 * blocked by an ad blocker). Stored on the CRM lead so the team can watch how someone used the demo.
 */
export const currentReplayUrl = (): string | undefined => {
  try {
    if (!posthog.__loaded || !posthog.get_session_id()) return undefined;
    return posthog.get_session_replay_url() || undefined;
  } catch {
    return undefined;
  }
};
